import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { getImageUrl } from '../data/cakes';
import { api } from '../utils/api';
import Toast from '../components/Toast';
import Footer from '../components/Footer';
import './CakeDetails.css';

const CakeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addCake } = useCart();
  
  const [cake, setCake] = useState(null);
  const [weight, setWeight] = useState('');
  const [flavour, setFlavour] = useState('');
  const [quantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState(null);

  useEffect(() => {
    loadCake();
  }, [id]);

  // Set default weight and flavour when cake loads
  useEffect(() => {
    if (cake) {
      // Set default weight to base_weight
      if (cake.base_weight) {
        setWeight(parseFloat(cake.base_weight).toString());
      }
      
      // Set default flavour to first available flavour
      if (cake.flavours && cake.flavours.length > 0) {
        setFlavour(JSON.stringify(cake.flavours[0]));
      }
    }
  }, [cake]);

  const loadCake = async () => {
    setLoading(true);
    try {
      // Fetch all cakes since there's no single cake endpoint
      const data = await api.get('/cakes.php');
      
      if (data.success && data.data && data.data.cakes) {
        // Find the specific cake by ID
        const foundCake = data.data.cakes.find(c => c.id === parseInt(id));
        
        if (foundCake) {
          setCake(foundCake);
        } else {
          console.error('Cake not found');
          navigate('/');
        }
      } else {
        console.error('Failed to load cakes');
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading cake:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    if (!cake) return 0;
    
    const basePrice = parseFloat(cake.base_price) || 0;
    const baseWeight = parseFloat(cake.base_weight) || 1;
    const customFee = parseFloat(cake.custom_design_fee) || 0;
    
    if (!weight || !flavour) {
      return basePrice + customFee;
    }

    // Calculate cake price: base price for base weight, 75% increase per 500gm above base
    const selectedWeight = parseFloat(weight);
    
    let cakePrice = basePrice; // Start with base price
    
    // Only apply increment if weight is more than base weight
    if (selectedWeight > baseWeight) {
      const additionalWeight = selectedWeight - baseWeight;
      const pricePerHalfKg = basePrice / (baseWeight / 0.5); // Price per 500gm at base
      const additionalHalfKgs = additionalWeight / 0.5; // Additional 500gm units
      
      // Each additional 500gm costs 75% more than previous
      const baseHalfKgs = baseWeight / 0.5; // Number of base units
      for (let i = 0; i < additionalHalfKgs; i++) {
        cakePrice += pricePerHalfKg * Math.pow(1.75, baseHalfKgs + i) / Math.pow(1.75, baseHalfKgs - 1);
      }
    } else if (selectedWeight < baseWeight) {
      // If less than base weight, scale down proportionally
      cakePrice = basePrice * (selectedWeight / baseWeight);
    }
    
    const selectedFlavour = JSON.parse(flavour);
    const flavourPrice = parseFloat(selectedFlavour.price_per_half_kg) * (selectedWeight / 0.5);
    
    return cakePrice + customFee + flavourPrice;
  };

  const getPriceBreakdown = () => {
    if (!cake) return { base: 0, customFee: 0, flavour: 0 };
    
    const basePrice = parseFloat(cake.base_price) || 0;
    const baseWeight = parseFloat(cake.base_weight) || 1;
    const customFee = parseFloat(cake.custom_design_fee) || 0;
    
    const breakdown = {
      base: basePrice,
      customFee: customFee,
      flavour: 0
    };

    if (weight && flavour) {
      const selectedWeight = parseFloat(weight);
      let cakePrice = basePrice;
      
      if (selectedWeight > baseWeight) {
        const additionalWeight = selectedWeight - baseWeight;
        const pricePerHalfKg = basePrice / (baseWeight / 0.5);
        const additionalHalfKgs = additionalWeight / 0.5;
        const baseHalfKgs = baseWeight / 0.5;
        
        for (let i = 0; i < additionalHalfKgs; i++) {
          cakePrice += pricePerHalfKg * Math.pow(1.75, baseHalfKgs + i) / Math.pow(1.75, baseHalfKgs - 1);
        }
      } else if (selectedWeight < baseWeight) {
        cakePrice = basePrice * (selectedWeight / baseWeight);
      }
      breakdown.base = cakePrice;
      
      const selectedFlavour = JSON.parse(flavour);
      breakdown.flavour = parseFloat(selectedFlavour.price_per_half_kg) * (selectedWeight / 0.5);
    }

    return breakdown;
  };

  const handleAddToCart = () => {
    if (!weight || !flavour) {
      // Show error toast if weight/flavour not selected
      if (window.plugins && window.plugins.toast) {
        window.plugins.toast.showShortCenter('Please select weight and flavour');
      } else {
        alert('Please select weight and flavour');
      }
      return;
    }

    const selectedFlavour = JSON.parse(flavour);

    addCake({
      cakeId: cake.id,
      cakeTitle: cake.title,
      cakeImage: cake.image,
      weight: parseFloat(weight),
      flavourId: selectedFlavour.id,
      flavourName: selectedFlavour.flavour_name,
      flavourPrice: selectedFlavour.price_per_half_kg,
      basePrice: cake.base_price,
      baseWeight: cake.base_weight,
      customDesignFee: cake.custom_design_fee || 0,
      quantity: quantity
    });

    // Show success toast
    if (window.plugins && window.plugins.toast) {
      // Use Cordova toast plugin on device
      window.plugins.toast.showShortCenter('🎉 Cake added to cart!');
    } else {
      // Use custom toast component for browser
      setToastMessage('🎉 Cake added to cart!');
    }
    
    // Navigate to cart after a short delay
    setTimeout(() => {
      navigate('/cart');
    }, 500);
  };

  if (loading) {
    return (
      <div className="cake-details-page">
        <div className="details-header glass-effect">
          <Link to="/" className="back-button ripple">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </Link>
          <h1 className="details-title">Loading...</h1>
        </div>
        <div className="loading-container">
          <div className="skeleton" style={{ height: '300px', marginBottom: '1rem' }}></div>
          <div className="skeleton" style={{ height: '40px', marginBottom: '1rem' }}></div>
          <div className="skeleton" style={{ height: '80px' }}></div>
        </div>
      </div>
    );
  }

  if (!cake) {
    return null;
  }

  const totalPrice = calculatePrice();
  const breakdown = getPriceBreakdown();
  const imageUrl = getImageUrl(cake.image);

  // Generate weight options
  const weightOptions = [];
  const startWeight = cake ? Math.max(0.5, parseFloat(cake.base_weight) || 0.5) : 0.5;
  
  for (let w = startWeight; w <= 10; w += 0.5) {
    weightOptions.push(w);
  }

  return (
    <div className="cake-details-page">
      <div className="details-header glass-effect">
        <Link to="/" className="back-button ripple">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
          </svg>
        </Link>
        <h1 className="details-title">{cake.title}</h1>
      </div>

      <div className="details-content">
        {imageUrl && (
          <div className="details-image-container">
            <img src={imageUrl} alt={cake.title} className="details-image" />
            {cake.custom_design_fee > 0 && (
              <div className="custom-badge">
                <span>✨ Custom Design</span>
              </div>
            )}
          </div>
        )}

        <div className="details-info">
          <div className="cake-header-info">
            <h2 className="details-cake-title">{cake.title}</h2>
            {cake.description && (
            <div className="description-section">
              <p className="description-text">{cake.description}</p>
            </div>
          )}
            <div className="cake-price-badge">
              ₹{Math.round(cake.base_price).toLocaleString()}
              {cake.custom_design_fee > 0 && (
                <span className="custom-fee-text">+₹{cake.custom_design_fee} custom</span>
              )}
            </div>
          </div>

          

          <div className="specs-row">
            <div className="spec-badge">
              <svg className="spec-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"></path>
              </svg>
              <span>{cake.base_weight}kg</span>
            </div>
            <div className="spec-badge">
              <svg className="spec-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
              <span>{cake.serves_people}</span>
            </div>
            {/*<div className="spec-badge">
              <span>{cake.category_name}</span>
            </div>*/}
          </div>

          <div className="selection-section">
            <h3 className="section-heading">Customize Your Order</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Weight</label>
                <select 
                  className="form-select"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                >
                  <option value="">Select</option>
                  {weightOptions.map(w => (
                    <option key={w} value={w}>{w.toFixed(1)} kg</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Flavour</label>
                <select 
                  className="form-select"
                  value={flavour}
                  onChange={(e) => setFlavour(e.target.value)}
                >
                  <option value="">Select</option>
                  {cake.flavours && cake.flavours.map(f => (
                    <option key={f.id} value={JSON.stringify(f)}>
                      {f.flavour_name} {f.price_per_half_kg > 0 ? `(+₹${f.price_per_half_kg}/500g)` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {(weight || flavour) && (
              <div className="price-summary">
                <div className="price-row">
                  <span>Base Price</span>
                  <span>₹{Math.round(breakdown.base).toLocaleString()}</span>
                </div>
                
                {breakdown.customFee > 0 && (
                  <div className="price-row">
                    <span>Custom Design</span>
                    <span>₹{Math.round(breakdown.customFee).toLocaleString()}</span>
                  </div>
                )}
                
                {breakdown.flavour > 0 && (
                  <div className="price-row">
                    <span>Flavour Charge</span>
                    <span>₹{Math.round(breakdown.flavour).toLocaleString()}</span>
                  </div>
                )}
                
                <div className="price-total">
                  <span>Total</span>
                  <span className="total-amount">₹{Math.round(totalPrice).toLocaleString()}</span>
                </div>
              </div>
            )}

            <button className="btn-add-to-cart btn-primary ripple" onClick={handleAddToCart}>  
              Add to Cart
            </button>
          </div>
        </div>
      </div>
      
      {/* Toast notification for browser */}
      {toastMessage && (
        <Toast 
          message={toastMessage} 
          onClose={() => setToastMessage(null)}
        />
      )}
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CakeDetails;
